import { defineStore } from 'pinia'
import axios from '@/services'

export const useCategoriesStore = defineStore('categories', {
  modelName: 'Category',
  state: () => ({
    categories: [],
    categoryTree: [],
    loading: false,
    error: null,
    items: [],
    totalItems: 0
  }),

  getters: {
    getAllCategories: (state) => state.categories,
    categoryTreeData: (state) => state.categoryTree,
    getItems: (state) => state.items,
    getTotalItems: (state) => state.totalItems,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    categoriesCount: (state) => state.categories.length
  },

  actions: {
    onModelChanged() {
      this.getCategories()
    },

    setLoading(loading) {
      this.loading = loading
    },

    setError(error) {
      this.error = error
    },

    async getCategories() {
      this.loading = true
      this.error = null

      try {
        const response = await axios.get('/api/categories/getCategories')
        this.categories = response.data.data || response.data || []
        return response.data
      } catch (error) {
        console.error('Error fetching categories:', error)
        this.error = error.message
        this.categories = []
        throw error
      } finally {
        this.loading = false
      }
    },

    async getCategoriesTree() {
      try {
        this.setLoading(true)
        this.setError(null)

        const response = await axios.get('/api/categories/tree')
        this.categoryTree = response.data.data || response.data || []

        return this.categoryTree
      } catch (error) {
        this.setError(error.message)
        console.error('Error fetching categories tree:', error)
        throw error
      } finally {
        this.setLoading(false)
      }
    },

    // Alias para getCategoryTree (usado en componentes legacy)
    async getCategoryTree() {
      try {
        const response = await axios.get('/api/categories/getCategoryTree')

        if (response.status === 200) {
          this.categoryTree = response.data.data || []
          return this.categoryTree
        }

        return []
      } catch (error) {
        console.error('Error fetching category tree:', error)
        throw error
      }
    },

    async getCategoriesList(filters = {}) {
      try {
        this.setLoading(true)
        this.setError(null)

        const response = await axios.post('/api/categories/list', filters)

        // Manejar tanto formato tree como lista plana
        if (response.data.success) {
          this.items = response.data.data.categories || response.data.data || []
          this.totalItems = response.data.data.totalItems || response.data.total || 0
        } else {
          this.items = response.data.data || []
          this.totalItems = response.data.total || 0
        }

        return response.data
      } catch (error) {
        this.setError(error.message)
        console.error('Error fetching categories list:', error)
        throw error
      } finally {
        this.setLoading(false)
      }
    },

    // Obtener una categoría individual
    async getCategory(id) {
      try {
        this.setLoading(true)
        this.setError(null)

        const response = await axios.get(`/api/categories/getCategory/${id}`)

        return response.data.data
      } catch (error) {
        this.setError(error.message)
        console.error('Error fetching category:', error)
        throw error
      } finally {
        this.setLoading(false)
      }
    },

    async addCategory(category) {
      try {
        this.setLoading(true)
        this.setError(null)

        const response = await axios.post('/api/categories/add', category)

        // Actualizar listas locales
        if (response.data.data) {
          this.categories.push(response.data.data)
          this.items.push(response.data.data)
          this.totalItems += 1
        }

        return response.data
      } catch (error) {
        this.setError(error.message)
        throw error
      } finally {
        this.setLoading(false)
      }
    },

    async editCategory(id, category) {
      try {
        this.setLoading(true)
        this.setError(null)

        const response = await axios.post(`/api/categories/edit/${id}`, category)

        // Actualizar en listas locales
        const updateInArray = (array) => {
          const index = array.findIndex(item => item.id === id)
          if (index !== -1 && response.data.data) {
            array[index] = response.data.data
          }
        }

        updateInArray(this.categories)
        updateInArray(this.items)

        return response.data
      } catch (error) {
        this.setError(error.message)
        throw error
      } finally {
        this.setLoading(false)
      }
    },

    async deleteCategory(categoryData) {
      try {
        this.setLoading(true)
        this.setError(null)

        const response = await axios.post('/api/categories/delete', categoryData)

        // Remover de listas locales
        const removeFromArray = (array) => {
          return array.filter(item => item.id !== categoryData.id)
        }

        this.categories = removeFromArray(this.categories)
        this.items = removeFromArray(this.items)
        this.totalItems = Math.max(0, this.totalItems - 1)

        return response.data
      } catch (error) {
        this.setError(error.message)
        throw error
      } finally {
        this.setLoading(false)
      }
    },

    setCategories(categories) {
      this.categories = categories
    },

    setCategoryTree(tree) {
      this.categoryTree = tree
    },

    clearError() {
      this.error = null
    }
  }
})